import { SupabaseClient } from '@supabase/supabase-js';
import { notify } from '../lib/dialogs';

/**
 * A duplicação do lead (Produção / CS) copia só a linha de `leads`. Anotações e anexos
 * vivem em tabelas próprias ligadas ao lead por `lead_id`, então sem esta cópia o time de
 * destino recebia o lead "vazio" e precisava refazer o levantamento com o cliente.
 *
 * Anotações (aba Notas): copiadas com autor e data originais. As "Anotações de atendimento"
 * (leads.service_notes) e as "Observações gerais" (leads.notes) são colunas do lead e já vão na cópia da linha. Anexos: o arquivo é copiado no storage para
 * a pasta do novo lead (e não só a URL), porque ao apagar um lead seus arquivos são removidos
 * do bucket — compartilhar o mesmo arquivo faria um lead quebrar o anexo do outro.
 * Tarefas e mensagens de WhatsApp não são copiadas: as tarefas pendentes são do comercial e
 * as mensagens são únicas por `message_id`. Falhas aqui nunca derrubam a venda.
 */
export async function copyLeadRelatedData(
    supabase: SupabaseClient,
    companyId: number,
    fromLeadId: number,
    toLeadId: number
): Promise<void> {
    // Anotações: preferimos a função copy_lead_notes (migration 20261103000000), que copia no banco.
    // Pelo navegador a cópia passa pelo RLS de lead_notes, que esconde as notas de quem não é
    // dono/closer do lead nem 'gestor' (ex.: a CEO da Evolua dando ganho) e o CS recebia o card sem elas.
    // Se a função ainda não existir no banco, cai na cópia antiga abaixo.
    let notesCopiedByDb = false;
    try {
        const { error: rpcError } = await supabase.rpc('copy_lead_notes', { p_from_lead_id: fromLeadId, p_to_lead_id: toLeadId });
        if (rpcError) console.warn('copy_lead_notes indisponível, copiando anotações pelo cliente:', rpcError.message);
        else notesCopiedByDb = true;
    } catch (err) {
        console.warn('copy_lead_notes falhou, copiando anotações pelo cliente:', err);
    }
    if (!notesCopiedByDb) try {
        const { data: notes, error: notesError } = await supabase
            .from('lead_notes')
            .select('user_id, content, created_at, updated_at')
            .eq('lead_id', fromLeadId);
        if (notesError) throw notesError;
        if (notes && notes.length > 0) {
            const { error } = await supabase
                .from('lead_notes')
                .insert(notes.map(n => ({ ...n, lead_id: toLeadId })));
            if (error) console.error('Erro ao copiar anotações do lead:', error);
        }
    } catch (err) {
        console.error('Falha ao copiar anotações do lead:', err);
    }

    try {
        const { data: attachments, error: attError } = await supabase
            .from('lead_attachments')
            .select('user_id, file_url, file_name, file_type, file_size, category, created_at')
            .eq('lead_id', fromLeadId);
        if (attError) throw attError;

        for (const att of attachments || []) {
            let fileUrl = att.file_url as string;
            // URL pública: .../object/public/attachments/<company>/<lead>/<arquivo>
            const marker = '/attachments/';
            const idx = fileUrl.indexOf(marker);
            if (idx !== -1) {
                const oldPath = decodeURIComponent(fileUrl.slice(idx + marker.length).split('?')[0]);
                const fileName = oldPath.split('/').pop();
                const newPath = `${companyId}/${toLeadId}/${fileName}`;
                const { error: copyError } = await supabase.storage.from('attachments').copy(oldPath, newPath);
                if (copyError) {
                    console.error('Erro ao copiar arquivo do anexo, pulando:', att.file_name, copyError);
                    continue;
                }
                fileUrl = supabase.storage.from('attachments').getPublicUrl(newPath).data.publicUrl;
            }
            const { error } = await supabase.from('lead_attachments').insert({
                ...att,
                file_url: fileUrl,
                lead_id: toLeadId,
                company_id: companyId,
            });
            if (error) console.error('Erro ao copiar anexo do lead:', error);
        }
    } catch (err) {
        console.error('Falha ao copiar anexos do lead:', err);
    }
}

/**
 * Colunas GENERATED ALWAYS de `leads`. Inserir qualquer valor nelas derruba o INSERT inteiro
 * ("cannot insert a non-DEFAULT value into column ..."), então a cópia nunca pode levá-las.
 * Ao criar outra coluna gerada em `leads`, acrescente aqui.
 */
const LEAD_GENERATED_COLUMNS = [
    'search_text',  // 20261005000000_leads_search_unaccent
    'phone_digits', // 20261019030000_leads_phone_digits_search
];

/**
 * Linha de `leads` pronta para virar INSERT de uma cópia. Tira o que o banco gera sozinho:
 * `id`, datas e as colunas geradas (LEAD_GENERATED_COLUMNS). Foi uma coluna gerada esquecida
 * que impediu a criação dos cards de Produção/CS depois de cada uma dessas migrations.
 */
export function buildLeadCopy(existingLead: Record<string, unknown>): Record<string, unknown> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id: _id, created_at: _createdAt, updated_at: _updatedAt, ...leadCopy } = existingLead;
    for (const col of LEAD_GENERATED_COLUMNS) delete leadCopy[col];
    return leadCopy;
}

export async function duplicateLeadToProducao(
    supabase: SupabaseClient, 
    companyId: number, 
    leadId: number, 
    overrideValue?: number,
    overrideMetadata?: Record<string, unknown>,
    saleDate?: string
): Promise<number | null> {
    try {
        let producaoFunnel = null;

        const { data: explicitProducao } = await supabase
            .from('funnels')
            .select('id')
            .eq('company_id', companyId)
            .eq('role_type', 'operacao')
            .limit(1)
            .maybeSingle();
            
        if (explicitProducao) {
            producaoFunnel = explicitProducao;
        } else {
            const { data } = await supabase
                .from('funnels')
                .select('id')
                .eq('company_id', companyId)
                .or('name.ilike.%produ%,name.ilike.%opera%,name.ilike.%fábrica%,name.ilike.%fabrica%')
                .limit(1)
                .maybeSingle();
            producaoFunnel = data;
        }

        if (!producaoFunnel) {
            notify('Atenção: A venda foi registrada, mas não localizamos nenhum funil com o nome "Produção", "Operação" ou "Fábrica" para duplicar o lead.');
            return null;
        }

        const { data: producaoStage } = await supabase
            .from('funnel_stages')
            .select('id')
            .eq('funnel_id', producaoFunnel.id)
            .order('order_index', { ascending: true })
            .limit(1)
            .maybeSingle();

        if (!producaoStage) {
            notify('Atenção: O funil de Produção foi encontrado, mas ele não tem nenhuma etapa (coluna) cadastrada para receber o lead.');
            return null;
        }

        const { data: producaoFunnelFull } = await supabase
            .from('funnels')
            .select('assignment_type')
            .eq('id', producaoFunnel.id)
            .maybeSingle();

        const { data: existingLead } = await supabase
            .from('leads')
            .select('*')
            .eq('id', leadId)
            .single();

        if (existingLead) {
            const insertData: Record<string, unknown> = {
                ...buildLeadCopy(existingLead),
                funnel_id: producaoFunnel.id,
                status: producaoStage.id.toString(),
                created_at: saleDate ? new Date(saleDate).toISOString() : new Date().toISOString()
            };

            // Ver comentário equivalente em duplicateLeadToCs: não herdar o
            // responsável do lead de origem quando o funil de destino tem
            // sua própria regra de atribuição (direta/rodízio).
            const producaoAssignmentType = producaoFunnelFull?.assignment_type;
            if (producaoAssignmentType === 'direct' || producaoAssignmentType === 'round_robin') {
                insertData.user_id = null;
            }

            if (overrideValue !== undefined) {
                insertData.value = overrideValue;
            }
            if (overrideMetadata !== undefined) {
                insertData.metadata = overrideMetadata;
            }

            const { data: novoLead, error: dupError } = await supabase
                .from('leads')
                .insert(insertData)
                .select('id')
                .single();
            
            if (dupError) {
                console.error('Erro ao duplicar lead para Produção:', dupError);
                notify('Erro ao duplicar lead para a Produção: ' + dupError.message);
            }
            if (novoLead?.id) {
                await copyLeadRelatedData(supabase, companyId, leadId, novoLead.id);
            }
            return novoLead?.id ?? null;
        } else {
            notify('Atenção: O lead original não foi encontrado no banco de dados para ser duplicado.');
        }
        return null;
    } catch (err) {
        console.error('Falha inesperada ao duplicar lead:', err);
        return null;
    }
}

export async function duplicateLeadToCs(
    supabase: SupabaseClient, 
    companyId: number, 
    leadId: number, 
    overrideValue?: number,
    overrideMetadata?: Record<string, unknown>,
    turmaId?: number | null,
    saleDate?: string
): Promise<number | null> {
    try {
        let targetFunnelId = null;
        let targetFunnelAssignmentType: string | null = null;

        // Tenta buscar por role_type='cs' primeiro
        const { data: explicitCsFunnels } = await supabase
            .from('funnels')
            .select('id, assignment_type')
            .eq('company_id', companyId)
            .eq('role_type', 'cs')
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();

        if (explicitCsFunnels) {
            targetFunnelId = explicitCsFunnels.id;
            targetFunnelAssignmentType = explicitCsFunnels.assignment_type;
        } else {
            // Fallback para nome
            const { data: csFunnels } = await supabase
                .from('funnels')
                .select('id, assignment_type')
                .eq('company_id', companyId)
                .or('name.ilike.%pós%,name.ilike.%pos%,name.ilike.%turma%,name.ilike.%cs%,name.ilike.%onboarding%,name.ilike.%recepção%,name.ilike.%recepcao%')
                .order('created_at', { ascending: true });

            if (csFunnels && csFunnels.length > 0) {
                targetFunnelId = csFunnels[0].id;
                targetFunnelAssignmentType = csFunnels[0].assignment_type;
            }
        }

        if (!targetFunnelId) {
            console.warn('Atenção: A venda foi registrada, mas não localizamos nenhum funil de CS/Turmas para duplicar o lead.');
            return null;
        }

        const { data: posVendaStages } = await supabase
            .from('funnel_stages')
            .select('id, funnel_id, name, original_id')
            .eq('funnel_id', targetFunnelId) // funnel_stages não tem company_id
            .or('name.ilike.Novo,name.ilike.%Turma Marcada%')
            .order('order_index', { ascending: true });

        let csStageId = null;

        if (posVendaStages && posVendaStages.length > 0) {
            const firstStage = posVendaStages.find(s => s.name.toLowerCase() === 'novo');
            const turmaMarcadaStage = posVendaStages.find(s => s.name.toLowerCase().includes('turma marcada'));
            
            const targetStageForCursos = turmaId ? (turmaMarcadaStage || firstStage) : firstStage;

            if (targetStageForCursos) {
                csStageId = targetStageForCursos.original_id || targetStageForCursos.id.toString();
            }
        }

        if (!csStageId) {
            // Fallback para a primeira etapa do funil CS
            const { data: fallbackStage } = await supabase
                .from('funnel_stages')
                .select('id, original_id')
                .eq('funnel_id', targetFunnelId)
                .order('order_index', { ascending: true })
                .limit(1)
                .maybeSingle();
                
            if (fallbackStage) {
                csStageId = fallbackStage.original_id || fallbackStage.id.toString();
            } else {
                console.warn('Atenção: O funil de CS foi encontrado, mas não possui etapas.');
                return null;
            }
        }

        const { data: existingLead } = await supabase
            .from('leads')
            .select('*')
            .eq('id', leadId)
            .single();

        if (existingLead) {
            const insertData: Record<string, unknown> = {
                ...buildLeadCopy(existingLead),
                funnel_id: targetFunnelId,
                status: csStageId,
                turma_id: turmaId || null,
                created_at: saleDate ? new Date(saleDate).toISOString() : new Date().toISOString()
            };

            // Se o funil de destino tem regra própria de atribuição (direto/rodízio),
            // não carregamos o "responsável" do lead de origem: isso mascarava
            // funis mal configurados (ficava parecendo atribuído) e impedia o
            // rodízio de girar de fato. O gatilho assign_lead_from_funnel decide o dono.
            if (targetFunnelAssignmentType === 'direct' || targetFunnelAssignmentType === 'round_robin') {
                insertData.user_id = null;
            }

            if (overrideValue !== undefined) {
                insertData.value = overrideValue;
            }
            if (overrideMetadata !== undefined) {
                insertData.metadata = overrideMetadata;
            }

            const { data: novoLead, error: dupError } = await supabase
                .from('leads')
                .insert(insertData)
                .select('id')
                .single();

            if (dupError) {
                console.error('Erro ao duplicar lead para CS:', dupError);
                notify('Erro ao duplicar lead para o CS/Turmas: ' + dupError.message);
                return null;
            }
            if (novoLead?.id) {
                await copyLeadRelatedData(supabase, companyId, leadId, novoLead.id);
                return novoLead.id;
            }
        }
        return null;
    } catch (err) {
        console.error('Falha inesperada ao duplicar lead para CS:', err);
        return null;
    }
}

export type FaturamentoBucket =
    | 'Abaixo de R$ 10 Mil'
    | 'De R$ 10 a 20 Mil'
    | 'De R$ 21 a 50 Mil'
    | 'De R$ 51 a 100 Mil'
    | 'Acima de R$ 100 Mil'
    | 'Outro';

export const FATURAMENTO_BUCKETS: FaturamentoBucket[] = [
    'Abaixo de R$ 10 Mil',
    'De R$ 10 a 20 Mil',
    'De R$ 21 a 50 Mil',
    'De R$ 51 a 100 Mil',
    'Acima de R$ 100 Mil',
    'Outro',
];

// O campo metadata.faturamento_mensal do lead (faixa de faturamento mensal
// informada na qualificação) é texto livre — não é um select — então a mesma
// faixa aparece escrita de formas diferentes ("De R$ 10 a 20 MIL",
// "de_10_a_20_mil_", "acima_de_100_mil"...). Esta função agrupa tudo nas 5
// faixas usadas hoje, pra servir de filtro sem exigir que o dado já esteja
// limpo. Quando não reconhece o formato, cai em "Outro" — um balde catch-all
// visível é melhor que uma faixa errada silenciosa.
export function normalizeFaturamentoBucket(raw?: string | null): FaturamentoBucket | null {
    if (!raw || !raw.trim()) return null;
    const s = raw.toLowerCase().replace(/[._]/g, ' ').replace(/\s+/g, ' ').trim();

    if (/\babaixo\b/.test(s) && /\b10\b/.test(s)) return 'Abaixo de R$ 10 Mil';
    if (/\bacima\b/.test(s) || /\+\s*100\s*k\b/.test(s)) return 'Acima de R$ 100 Mil';
    if (/\b21\b/.test(s) && /\b50\b/.test(s)) return 'De R$ 21 a 50 Mil';
    if (/\b(50|51)\b/.test(s) && /\b100\b/.test(s)) return 'De R$ 51 a 100 Mil';
    if (/\b10\b/.test(s) && /\b20\b/.test(s)) return 'De R$ 10 a 20 Mil';

    // Sobrou um número solto (sem faixa por extenso): assume valor em reais cheio.
    const digitsOnly = s.replace(/[^\d]/g, '');
    if (digitsOnly && /^[\d.,\s]+$/.test(s)) {
        const numeric = Number(digitsOnly);
        if (numeric > 0) {
            if (numeric < 10000) return 'Abaixo de R$ 10 Mil';
            if (numeric <= 20000) return 'De R$ 10 a 20 Mil';
            if (numeric <= 50000) return 'De R$ 21 a 50 Mil';
            if (numeric <= 100000) return 'De R$ 51 a 100 Mil';
            return 'Acima de R$ 100 Mil';
        }
    }

    return 'Outro';
}
